import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./EnquiriesPage.css";

const STATUS_OPTIONS = ["New", "Read", "Resolved"];

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "N/A";
  }
};

const statusModifier = (status) => {
  switch (status) {
    case "Read":
      return "read";
    case "Resolved":
      return "resolved";
    default:
      return "new";
  }
};

const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [copied, setCopied] = useState("");

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getContactForms();
    setLoading(false);
    if (res?.success) {
      setEnquiries(Array.isArray(res.data) ? res.data : []);
    } else {
      setError(res?.message || "Failed to load enquiries.");
      setEnquiries([]);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied((prev) => (prev === key ? "" : prev)), 1500);
  };

  const handleSetStatus = async (id, status) => {
    setUpdatingId(id);
    const res = await AdminService.setContactFormStatus(id, status);
    setUpdatingId("");
    if (res?.success) {
      setEnquiries((prev) =>
        prev.map((row) =>
          row._id === id
            ? { ...row, status, updatedAt: new Date().toISOString() }
            : row
        )
      );
    } else {
      setError(res?.message || "Failed to update status.");
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enquiries.filter((row) => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (!term) return true;
      return [row.name, row.email, row.phone, row.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [enquiries, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { New: 0, Read: 0, Resolved: 0 };
    enquiries.forEach((row) => {
      if (c[row.status] != null) c[row.status] += 1;
    });
    return c;
  }, [enquiries]);

  return (
    <main className="dashboard-main dashboard-subpage enquiries-page">
      <section className="dashboard-hero enquiries-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Enquiries</span>
          </p>
          <h1 className="dashboard-hero__title">Enquiries</h1>
          <p className="dashboard-hero__subtitle">
            Contact form submissions from your website
          </p>
        </div>
        <div className="enquiries-hero__icon">
          <i className="fas fa-envelope-open-text" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="enquiries-section">
          <div className="enquiries-header">
            <h2 className="enquiries__title">
              <i className="fas fa-inbox" /> Submissions
            </h2>
            <button
              type="button"
              className="enquiries-btn-refresh"
              onClick={fetchEnquiries}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
            </button>
          </div>

          <div className="enquiries-filters">
            <div className="enquiries-filters__row">
              <label className="enquiries-filter">
                <span>Search</span>
                <input
                  type="text"
                  placeholder="Name, email, phone, message..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <label className="enquiries-filter">
                <span>Status</span>
                <select
                  className="enquiries-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All ({enquiries.length})</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s} ({counts[s] ?? 0})
                    </option>
                  ))}
                </select>
              </label>
              {(search || statusFilter) && (
                <button
                  type="button"
                  className="enquiries-btn-reset"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                  }}
                >
                  <i className="fas fa-undo" /> Reset
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="enquiries-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="enquiries-table-wrap">
            {loading ? (
              <div className="enquiries-loading">
                <i className="fas fa-spinner fa-spin" /> Loading enquiries...
              </div>
            ) : filtered.length === 0 ? (
              <div className="enquiries-empty">
                <i className="fas fa-inbox" />
                <p>No enquiries found.</p>
              </div>
            ) : (
              <table className="enquiries-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Received</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row._id}>
                      <td>
                        <span className="enquiries-name">{row.name || "—"}</span>
                      </td>
                      <td>
                        {row.email ? (
                          <button
                            type="button"
                            className="enquiries-contact"
                            title="Click to copy"
                            onClick={() => handleCopy(`${row._id}-email`, row.email)}
                          >
                            <i
                              className={`fas ${
                                copied === `${row._id}-email` ? "fa-check" : "fa-envelope"
                              }`}
                            />{" "}
                            {copied === `${row._id}-email` ? "Copied!" : row.email}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {row.phone ? (
                          <button
                            type="button"
                            className="enquiries-contact"
                            title="Click to copy"
                            onClick={() => handleCopy(`${row._id}-phone`, row.phone)}
                          >
                            <i
                              className={`fas ${
                                copied === `${row._id}-phone` ? "fa-check" : "fa-phone"
                              }`}
                            />{" "}
                            {copied === `${row._id}-phone` ? "Copied!" : row.phone}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="enquiries-message">{row.message || "—"}</td>
                      <td>
                        <span
                          className={`enquiries-status enquiries-status--${statusModifier(row.status)}`}
                        >
                          {row.status || "—"}
                        </span>
                      </td>
                      <td className="enquiries-date">{formatDateTime(row.createdAt)}</td>
                      <td>
                        <div className="enquiries-actions">
                          <button
                            type="button"
                            className="enquiries-action-btn enquiries-action-btn--read"
                            disabled={updatingId === row._id || row.status === "Read"}
                            onClick={() => handleSetStatus(row._id, "Read")}
                          >
                            {updatingId === row._id ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-check" />
                            )}{" "}
                            Read
                          </button>
                          <button
                            type="button"
                            className="enquiries-action-btn enquiries-action-btn--resolved"
                            disabled={updatingId === row._id || row.status === "Resolved"}
                            onClick={() => handleSetStatus(row._id, "Resolved")}
                          >
                            {updatingId === row._id ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-check-double" />
                            )}{" "}
                            Resolved
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default EnquiriesPage;
